// src/lib/audit.ts
/**
 * Audit utilities
 * -----------------------------------------------------------------------------
 * Lightweight, permissive audit logger used across the app.
 *
 * Why this file?
 * - Many call sites pass extra fields to `logAudit(...)` (e.g., { id, route, count }).
 *   The previous typing was too strict and caused TS2353 errors. This version
 *   purposefully allows arbitrary metadata while still requiring an `action`.
 *
 * Storage:
 * - Primary data key: "audit:data:v1"
 * - For backward-compat with older code that (incorrectly) read from a table
 *   prefs key, we mirror writes to "tbl:audit-log:v1" as well.
 *
 * API:
 *   logAudit(payload, role?, actor?)
 *   readAudit()
 *   clearAudit()
 */

import { get, set } from "@/lib/storage";

export type AuditEntry = {
  id: string;                // uuid
  ts: string;                // ISO timestamp
  action: string;            // required action label
  role?: string;             // UI role at time of event
  actor?: string;            // optional user/actor label
  // Allow any additional metadata (route, id, count, to, reason, etc.)
  [key: string]: any;
};

const DATA_KEY = "audit:data:v1";       // canonical audit data store
const MIRROR_KEY = "tbl:audit-log:v1";  // compatibility mirror

function nowIso() {
  return new Date().toISOString();
}

function uuid() {
  // crypto.randomUUID is widely available in modern browsers / vite envs.
  // Fallback is not necessary for our target, but included for safety.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as any).randomUUID();
  }
  // naive fallback
  return "au_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Append an audit entry. Accepts any extra fields for flexibility.
 *
 * @param payload - must include { action: string }, plus any extra metadata
 * @param role    - optional current UI role (Admin/Dispatcher/etc.)
 * @param actor   - optional actor/user descriptor
 * @returns the saved AuditEntry
 */
export function logAudit(
  payload: { action: string } & Record<string, any>,
  role?: string,
  actor?: string
): AuditEntry {
  const entry: AuditEntry = {
    id: uuid(),
    ts: nowIso(),
    role,
    actor,
    ...payload,
  };

  // Read existing logs (prefer canonical key; fall back to mirror)
  const existing: AuditEntry[] =
    get<AuditEntry[]>(DATA_KEY, undefined) ??
    get<AuditEntry[]>(MIRROR_KEY, []) ??
    [];

  const next = [...existing, entry];

  // Write to both stores to keep older pages happy.
  set(DATA_KEY, next);
  set(MIRROR_KEY, next);

  return entry;
}

/**
 * Read all audit entries (from canonical store; falls back to mirror).
 */
export function readAudit(): AuditEntry[] {
  return (
    get<AuditEntry[]>(DATA_KEY, undefined) ??
    get<AuditEntry[]>(MIRROR_KEY, []) ??
    []
  );
}

/**
 * Clear all audit entries.
 */
export function clearAudit(): void {
  set(DATA_KEY, []);
  set(MIRROR_KEY, []);
}

export default {
  logAudit,
  readAudit,
  clearAudit,
};
